/**
 * Redux Persist Storage Configuration
 *
 * This utility creates a storage configuration that handles both client-side and server-side rendering.
 * It automatically falls back to noop storage when localStorage is not available (e.g., during SSR)
 * or when there are storage access issues.
 *
 * This prevents the "redux-persist failed to create sync storage. falling back to noop storage" error
 * and ensures proper hydration in Next.js applications.
 */

import { Storage } from 'redux-persist'

// Create a storage configuration that handles SSR and storage errors gracefully
export const createPersistStorage = (): Storage => {
  if (typeof window === 'undefined') {
    // Server-side: return noop storage
    return {
      getItem: () => Promise.resolve(null),
      setItem: () => Promise.resolve(),
      removeItem: () => Promise.resolve()
    }
  }

  // Client-side: try to use localStorage
  try {
    // Test if localStorage is available and working
    const testKey = '__redux_persist_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)

    // Return the standard redux-persist storage
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const storage = require('redux-persist/lib/storage').default

    // Additional validation that the storage object has the required methods
    if (storage && typeof storage.getItem === 'function' && typeof storage.setItem === 'function') {
      return storage
    } else {
      throw new Error('Storage object is invalid')
    }
  } catch (error) {
    // If localStorage fails, return noop storage
    console.warn('redux-persist: localStorage not available or invalid, falling back to noop storage:', error)

    return {
      getItem: () => Promise.resolve(null),
      setItem: () => Promise.resolve(),
      removeItem: () => Promise.resolve()
    }
  }
}
